// Cadastro de Produtos/Insumos (issue #7). Base do módulo de Estoque.

import { newId, type Id } from "./ids.ts";
import type { Insumo, Snapshot } from "./snapshot.ts";

export type { Insumo };

export interface DadosInsumo {
  nome: string;
  categoria: string;
  unidade: string;
  marca: string;
  fornecedor: string;
}

export class ErroInsumo extends Error {}

/**
 * Identidade do Insumo = (Nome, Marca), ambos case-insensitive — dois Insumos só
 * colidem se tiverem o mesmo Nome E a mesma Marca (issue #67). `estoqueMinimo`
 * não faz parte do cadastro: sai do formulário e não é validado aqui (ADR-0017).
 */
function validar(
  dados: DadosInsumo,
  snapshot: Snapshot,
  ignorarId?: Id,
): Omit<Insumo, "id" | "ativo" | "estoqueMinimo"> {
  const nome = dados.nome.trim();
  if (nome === "") throw new ErroInsumo("Informe o nome do insumo.");
  const marca = dados.marca.trim();
  if (
    snapshot.insumos.some(
      (i) =>
        i.id !== ignorarId &&
        i.nome.toLowerCase() === nome.toLowerCase() &&
        (i.marca ?? "").toLowerCase() === marca.toLowerCase(),
    )
  ) {
    throw new ErroInsumo(
      marca === ""
        ? `Já existe o insumo "${nome}".`
        : `Já existe o insumo "${nome}" da marca "${marca}".`,
    );
  }
  const unidade = dados.unidade.trim();
  if (unidade === "") throw new ErroInsumo("Informe a unidade de medida.");
  return {
    nome,
    categoria: dados.categoria.trim(),
    unidade,
    marca: marca === "" ? null : marca,
    fornecedor: dados.fornecedor.trim() === "" ? null : dados.fornecedor.trim(),
  };
}

export function criarInsumo(
  snapshot: Snapshot,
  dados: DadosInsumo,
  /** Id gerado no cliente (ADR-0003). Omitido = o domínio gera. */
  id: Id = newId(),
): Snapshot {
  const i: Insumo = {
    id,
    ...validar(dados, snapshot),
    estoqueMinimo: 0,
    ativo: true,
  };
  return { ...snapshot, insumos: [...snapshot.insumos, i] };
}

export function editarInsumo(
  snapshot: Snapshot,
  id: Id,
  dados: DadosInsumo,
): Snapshot {
  if (!snapshot.insumos.some((i) => i.id === id)) {
    throw new ErroInsumo("Insumo não encontrado.");
  }
  // `campos` não inclui `estoqueMinimo`: o valor gravado (ADR-0017) atravessa
  // a edição intocado, via o spread de `i` por baixo.
  const campos = validar(dados, snapshot, id);
  return {
    ...snapshot,
    insumos: snapshot.insumos.map((i) => (i.id === id ? { ...i, ...campos } : i)),
  };
}

export function definirAtivoInsumo(
  snapshot: Snapshot,
  id: Id,
  ativo: boolean,
): Snapshot {
  if (!snapshot.insumos.some((i) => i.id === id)) {
    throw new ErroInsumo("Insumo não encontrado.");
  }
  return {
    ...snapshot,
    insumos: snapshot.insumos.map((i) => (i.id === id ? { ...i, ativo } : i)),
  };
}
